import { createContext, Dispatch, SetStateAction } from "react";
import {
  BaseBotData,
  BaseBotGeneralData,
  BotData,
  BotGeneralData,
  ClientData,
} from "@shared/types";
import {
  MarginData,
  PendingMarginType,
  PendingMultiplier,
  SymbolMetadata,
  SymbolOption,
  SymbolToUpdatedMarginData,
} from "@shared/features/bots/BotForm/MultiSymbolMarginSelector/types";
import { BotFormData } from "@shared/features/bots/BotForm/common/BotForm.context";
import {
  MultiSymbolMarginData,
  SymbolMarginData,
} from "@shared/features/bots/BotForm/common/types";

export const MultiSymbolMarginContext = createContext<
  | {
      isViewOnly?: boolean;
      isSymbolsOnly?: boolean;
      botGeneralData?: BotGeneralData | BaseBotGeneralData | BotData;
      pendingGlobalMarginType: PendingMarginType;
      setPendingGlobalMarginType: (
        pendingGlobalMarginType: PendingMarginType
      ) => void;
      pendingGlobalMultiplier: MarginData["multiplier"];
      setPendingGlobalMultiplier: (
        pendingGlobalMultiplier: MarginData["multiplier"]
      ) => void;
      pendingGlobalLeverageType: MarginData["leverageType"];
      setPendingGlobalLeverageType: (
        pendingGlobalLeverageType: MarginData["leverageType"]
      ) => void;
      pendingMarginType: PendingMarginType;
      setPendingMarginType: (pendingMarginType: PendingMarginType) => void;
      pendingMultiplier: MarginData["multiplier"];
      setPendingMultiplier: (
        pendingMultiplier: MarginData["multiplier"]
      ) => void;
      pendingLeverageType: MarginData["leverageType"];
      setPendingLeverageType: (
        pendingLeverageType: MarginData["leverageType"]
      ) => void;
      selectedSymbols: Set<string>;
      setSelectedSymbols: (selectedSymbols: Set<string>) => void;
      modifiedPendingValues: Set<keyof MarginData>;
      setModifiedPendingValues: Dispatch<SetStateAction<Set<keyof MarginData>>>;
      isSearchOpen: boolean;
      setIsSearchOpen: (isSearchOpen: boolean) => void;
      searchValue: string | undefined;
      setSearchValue: (searchValue: string | undefined) => void;
      showOnlyModifiedSymbols: boolean;
      setShowOnlyModifiedSymbols: (showOnlyModifiedSymbols: boolean) => void;
      resetData: (
        resetGlobal?: boolean,
        resetSymbols?: boolean,
        resetData?: Set<string>
      ) => void;
      allSymbols: string[];
      filteredSymbols: string[];
      searchFilteredSymbols: string[];
      botFormDataRef: React.RefObject<BotFormData | undefined>;
      multiSymbolMarginData: MultiSymbolMarginData | undefined;
      updatedMultiSymbolMarginData: SymbolToUpdatedMarginData;
      setSelectedSymbolsAndPendingValues: (symbols: Set<string>) => void;
      lastModifiedMultiSymbolMarginData: MultiSymbolMarginData;
      hasMaxMultiplierError?: boolean;
      hasLeverageValueModifiedError?: boolean;
      hasPendingChanges?: boolean;
      selectorPreview: string;
      symbolOptions?: SymbolOption[];
      account: symbol | ClientData | undefined;
      alwaysDisplay?: boolean;
      botData?: BaseBotData | BotData;
      globalSettingsExpanded: boolean;
      setGlobalSettingsExpanded: (globalSettingsExpanded: boolean) => void;
      symbolToMarginMetadata: Record<string, SymbolMetadata>;
      initializedExchange?: string;
      isFullScreen?: boolean;
      updateLastModifiedData: (
        updatedLastModifiedData: MultiSymbolMarginData
      ) => void;
      applyMaxMultiplier: <T extends SymbolMarginData | undefined | MarginData>(
        marginData: T,
        symbol: string
      ) => T;
      getSymbolOriginalBotMultiplier: (symbol: string) => PendingMultiplier;
    }
  | undefined
>(undefined);
